import React from "react";
import { List, Text, Group, Menu, ActionIcon, Tooltip } from "@mantine/core";
import { IconArrowsExchange } from "@tabler/icons-react";
import { Employee } from "../../types/salon";
import { StaffTier } from "../../types/staffTier";

export const staffLabel = (e: Employee): string =>
  `${e.first_name} ${e.last_name}`.trim() || e.email;

interface StaffTierMembersCellProps {
  tier: StaffTier;
  tiers: StaffTier[];
  members: Employee[];
  canAssign: boolean;
  /** Move a member to another tier (StaffTier) or unparent (null). */
  onMove: (staffId: string, target: StaffTier | null, label: string) => void;
}

/**
 * The Members column for a tier row: a numbered list of assigned staff, each
 * with a move control that reassigns them to another tier or unparents them.
 */
const StaffTierMembersCell: React.FC<StaffTierMembersCellProps> = ({
  tier,
  tiers,
  members,
  canAssign,
  onMove,
}) => {
  if (members.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No staff assigned
      </Text>
    );
  }

  const otherTiers = tiers.filter((t) => t.id !== tier.id);

  return (
    <List type="ordered" size="sm" spacing={4}>
      {members.map((member) => (
        <List.Item key={member.id}>
          <Group gap={6} wrap="nowrap" justify="space-between">
            <Text size="sm">{staffLabel(member)}</Text>
            {canAssign && (
              <Menu position="bottom-end" withinPortal shadow="md">
                <Menu.Target>
                  <Tooltip label="Move to another tier">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Move ${staffLabel(member)}`}
                    >
                      <IconArrowsExchange size={15} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Move to</Menu.Label>
                  {otherTiers.map((t) => (
                    <Menu.Item
                      key={t.id}
                      onClick={() => onMove(member.id, t, staffLabel(member))}
                    >
                      {t.name}
                    </Menu.Item>
                  ))}
                  {otherTiers.length > 0 && <Menu.Divider />}
                  <Menu.Item
                    color="red"
                    onClick={() => onMove(member.id, null, staffLabel(member))}
                  >
                    Remove from tier
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </List.Item>
      ))}
    </List>
  );
};

export default StaffTierMembersCell;
